import { UserRole, OrganizationRole } from "@/types/enums";
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button
} from '@mui/material';
import {
  Edit as EditIcon,
  Groups as GroupsIcon,
  Sports as SportsIcon,
  Tag as TagIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Player } from '@/services/playerService';
import { useAppSelector } from '@/hooks/redux';
import { TeamLogo } from '@/components/TeamLogo';
import { PlayerAvatar } from '@/components/player/PlayerAvatar';

interface PlayerCardProps {
  player: Player;
  onDelete?: (player: Player) => void;
}

const PlayerCard: React.FC<PlayerCardProps> = ({
  player,
  onDelete
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  
  // Check if current user can modify this player
  const currentOrgMembership = user?.availableOrganizations?.find(
    org => org.organizationId === user?.currentOrganizationId
  );
  const isOrgOwnerOrAdmin = currentOrgMembership?.role === OrganizationRole.OWNER || currentOrgMembership?.role === OrganizationRole.ADMIN;
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const isCreator = user?.parentId === player.parentId;
  
  const canModifyPlayer = isCreator || isOrgOwnerOrAdmin || isSuperAdmin;

  const handleEdit = () => {
    navigate(`/players/${player.id}?tab=profile`);
  };

  const handleManageTeams = () => {
    navigate(`/players/${player.id}?tab=teams`);
  };

  const getActiveAssignments = () => {
    return player.leagueAssignments?.filter(assignment => assignment.active) || [];
  };

  const activeAssignments = getActiveAssignments();

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column'
      }}
    >
      <CardContent sx={{ flex: 1, p: 3, pb: 2 }}>
        {/* Player Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          minHeight: '100px' // Increased height for two-line stats
        }}>
          <Box 
            sx={{ 
              position: 'relative',
              mr: 2
            }}
          >
            <PlayerAvatar
              playerId={player.id}
              player={player}
              size="medium"
              sx={{
                width: 56,
                height: 56,
                boxShadow: 2
              }}
            />
          </Box>
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontWeight: 600,
                lineHeight: 1.2,
                mb: 0.5,
                color: 'text.primary'
              }}
            >
              {player.name}
            </Typography>
            
            {/* Player Stats Row - Reserve consistent space */}
            <Box sx={{ 
              minHeight: '48px', // Increased height for two lines of data
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: 1.5, 
              flexWrap: 'wrap'
            }}>
              {player.position && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SportsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {player.position}
                  </Typography>
                </Box>
              )}
              
              {player.jerseyNumber && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TagIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {player.jerseyNumber}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Team Assignments Summary */}
        <Box>
          <Typography 
            variant="subtitle2" 
            color="text.secondary" 
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 500, mb: 1.5 }}
          >
            <GroupsIcon fontSize="small" />
            Teams
          </Typography>

          {activeAssignments.length === 0 ? (
            <Box 
              sx={{ 
                textAlign: 'center',
                py: 3,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'grey.300'
              }}
            >
              <GroupsIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ fontWeight: 500 }}
              >
                No team assignments
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary"
              >
                Click the teams button to get started
              </Typography>
            </Box>
          ) : (
            <>
              {/* Team Cards */}
              <Box sx={{ mb: 1.5 }}>
                {activeAssignments.slice(0, 2).map((assignment) => (
                  <Box
                    key={assignment.id}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      bgcolor: 'primary.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'primary.100',
                      position: 'relative'
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <TeamLogo
                        teamId={assignment.teamId}
                        teamName={assignment.teamName}
                        size={24}
                        logoPath={assignment.teamLogoPath}
                      />
                      <Typography 
                        variant="body2" 
                        fontWeight={600} 
                        color="primary.dark"
                        noWrap
                      >
                        {assignment.teamName}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.25 }}
                      noWrap
                    >
                      {assignment.league.division ? `${assignment.league.name} - ${assignment.league.division}` : assignment.league.name}
                    </Typography>
                    
                    {(assignment.position || assignment.jerseyNumber) && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                        {assignment.position && (
                          <Chip
                            label={assignment.position}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        )}
                        {assignment.jerseyNumber && (
                          <Chip
                            label={assignment.jerseyNumber}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                ))}
                
                {activeAssignments.length > 2 && (
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      +{activeAssignments.length - 2} more team{activeAssignments.length - 2 !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                )}
              </Box>

            </>
          )}
        </Box>
      </CardContent>

      {/* Action Buttons - With rainbow effects */}
      <Box sx={{ px: 2, pb: 2, pt: 0, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, alignItems: 'start' }}>
        <Button
          size="small"
          startIcon={<EditIcon />}
          variant="outlined"
          onClick={handleEdit}
          disabled={!canModifyPlayer}
          sx={{ 
            fontSize: '11px', 
            justifyContent: 'center',
            position: 'relative',
            zIndex: 0,
            background: 'white',
            color: 'text.primary',
            borderColor: 'divider',
            '&:hover': { borderColor: 'divider', backgroundColor: 'white' },
            '&:disabled': { 
              background: 'grey.100',
              color: 'text.disabled',
              borderColor: 'divider'
            },
            '&::before': {
              content: '""',
              background: 'linear-gradient(45deg, #ff0000, #ff7300, #fffb00, #48ff00, #00ffd5, #002bff, #7a00ff, #ff00c8, #ff0000)',
              position: 'absolute',
              top: '-1px',
              left: '-1px',
              backgroundSize: '400%',
              zIndex: -1,
              filter: 'blur(5px)',
              width: 'calc(100% + 2px)',
              height: 'calc(100% + 2px)',
              animation: 'glowing 20s linear infinite',
              opacity: 0,
              transition: 'opacity 0.3s ease-in-out',
              borderRadius: 1
            },
            '&:hover::before': { opacity: 1 },
            '&::after': {
              zIndex: -1,
              content: '""',
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: 'white',
              left: 0,
              top: 0,
              borderRadius: 1
            },
            '@keyframes glowing': {
              '0%': { backgroundPosition: '0 0' },
              '50%': { backgroundPosition: '400% 0' },
              '100%': { backgroundPosition: '0 0' }
            }
          }}
        >
          Edit
        </Button>
        
        <Button
          size="small"
          startIcon={<GroupsIcon />}
          variant="outlined"
          onClick={handleManageTeams}
          disabled={!canModifyPlayer}
          sx={{ 
            fontSize: '11px', 
            justifyContent: 'center',
            position: 'relative',
            zIndex: 0,
            background: 'white',
            color: 'text.primary',
            borderColor: 'divider',
            '&:hover': { borderColor: 'divider', backgroundColor: 'white' },
            '&:disabled': { 
              background: 'grey.100',
              color: 'text.disabled',
              borderColor: 'divider'
            },
            '&::before': {
              content: '""',
              background: 'linear-gradient(45deg, #ff0000, #ff7300, #fffb00, #48ff00, #00ffd5, #002bff, #7a00ff, #ff00c8, #ff0000)',
              position: 'absolute',
              top: '-1px',
              left: '-1px',
              backgroundSize: '400%',
              zIndex: -1,
              filter: 'blur(5px)',
              width: 'calc(100% + 2px)',
              height: 'calc(100% + 2px)',
              animation: 'glowing 20s linear infinite',
              opacity: 0,
              transition: 'opacity 0.3s ease-in-out',
              borderRadius: 1
            },
            '&:hover::before': { opacity: 1 },
            '&::after': {
              zIndex: -1,
              content: '""',
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: 'white',
              left: 0,
              top: 0,
              borderRadius: 1
            },
            '@keyframes glowing': {
              '0%': { backgroundPosition: '0 0' },
              '50%': { backgroundPosition: '400% 0' },
              '100%': { backgroundPosition: '0 0' }
            }
          }}
        >
          Teams
        </Button>

        <Button
          size="small"
          startIcon={<DeleteIcon />}
          variant="outlined"
          onClick={() => onDelete?.(player)}
          disabled={!canModifyPlayer}
          sx={{ 
            fontSize: '11px', 
            justifyContent: 'center',
            position: 'relative',
            zIndex: 0,
            background: 'white',
            color: 'text.primary',
            borderColor: 'divider',
            '&:hover': { borderColor: 'divider', backgroundColor: 'white' },
            '&:disabled': { 
              background: 'grey.100',
              color: 'text.disabled',
              borderColor: 'divider'
            },
            '&::before': {
              content: '""',
              background: 'linear-gradient(45deg, #ff0000, #ff7300, #fffb00, #48ff00, #00ffd5, #002bff, #7a00ff, #ff00c8, #ff0000)',
              position: 'absolute',
              top: '-1px',
              left: '-1px',
              backgroundSize: '400%',
              zIndex: -1,
              filter: 'blur(5px)',
              width: 'calc(100% + 2px)',
              height: 'calc(100% + 2px)',
              animation: 'glowing 20s linear infinite',
              opacity: 0,
              transition: 'opacity 0.3s ease-in-out',
              borderRadius: 1
            },
            '&:hover::before': { opacity: 1 },
            '&::after': {
              zIndex: -1,
              content: '""',
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: 'white',
              left: 0,
              top: 0,
              borderRadius: 1
            },
            '@keyframes glowing': {
              '0%': { backgroundPosition: '0 0' },
              '50%': { backgroundPosition: '400% 0' },
              '100%': { backgroundPosition: '0 0' }
            }
          }}
        >
          Delete
        </Button>
      </Box>
    </Card>
  );
};

export default PlayerCard;
